import { useLocalSearchParams, router } from "expo-router";
import React, { useMemo } from "react";
import { ActivityIndicator, FlatList, Pressable, View } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

import { IdentityHeader } from "@/src/components/IdentityHeader";
import { Text } from "@/src/components/ui/BrandText";
import { Ionicons } from "@/src/components/ui/ionicons";
import { useAccountConnections } from "@/src/hooks/useAccountConnections";
import { usePublicProfile } from "@/src/hooks/usePublicProfile";
import { getIdentityBadges, getAccountType } from "@/src/lib/identity";
import { contentMaxWidths, readingContent } from "@/src/theme/layout";
import { border, surface, text as uiText } from "@/src/theme/tokens";
import type { AccountConnectionDirection } from "@/src/lib/follows";
import type { PublicProfile } from "@/src/types/models";

function routeParam(value: string | string[] | undefined) {
  return Array.isArray(value) ? value[0] ?? "" : value ?? "";
}

function identityFor(profile: PublicProfile, fallbackId = "") {
  const accountType = getAccountType(profile) ?? "student";
  const displayName = accountType === "institution"
    ? profile.institutionName?.trim() || profile.displayName?.trim() || profile.username || "École Neqraw"
    : profile.displayName?.trim() || profile.username || "Utilisateur";
  return {
    identityId: profile.identityId || ("id" in profile && typeof profile.id === "string" ? profile.id : "") || fallbackId,
    handle: profile.username || "",
    displayName,
    avatarUrl: profile.photoURL?.trim() || null,
    accountType,
    badges: profile.badges?.length ? profile.badges : getIdentityBadges(profile),
  } as const;
}

function messageForError(error: unknown) {
  const code = (error as { code?: unknown } | null)?.code;
  if (code === "permission-denied" || code === "firestore/permission-denied") {
    return "Cette liste n’est pas accessible pour le moment.";
  }
  return "Impossible de charger cette liste.";
}

export default function ProfileConnectionsScreen() {
  const params = useLocalSearchParams<{ id?: string | string[]; type?: string | string[] }>();
  const accountId = routeParam(params.id).trim();
  const direction: AccountConnectionDirection = routeParam(params.type) === "following"
    ? "following"
    : "followers";
  const { profile, loadingProfile, error: profileError, retry: retryProfile } = usePublicProfile(accountId);
  const {
    accounts,
    loading,
    loadingMore,
    hasMore,
    error,
    loadMore,
    retry,
  } = useAccountConnections(accountId, direction);
  const title = direction === "followers" ? "Abonnés" : "Abonnements";
  const emptyLabel = direction === "followers" ? "Aucun abonné pour le moment." : "Aucun abonnement pour le moment.";

  const header = useMemo(() => (
    <View style={{ gap: 12, marginBottom: 8 }}>
      <Pressable
        onPress={() => router.back()}
        accessibilityRole="button"
        accessibilityLabel="Retour au profil"
        style={{ alignSelf: "flex-start", flexDirection: "row", alignItems: "center", gap: 6, minHeight: 36, paddingHorizontal: 4 }}
      >
        <Ionicons name="arrow-back" size={18} color={uiText.primary} />
        <Text style={{ color: uiText.primary, fontWeight: "800" }}>Profil</Text>
      </Pressable>
      <View style={{ backgroundColor: surface.primary, borderColor: border.subtle, borderWidth: 1, borderRadius: 18, padding: 16, gap: 14 }}>
        {profile ? <IdentityHeader identity={identityFor(profile, accountId)} compact /> : null}
        <Text style={{ color: uiText.primary, fontSize: 20, fontWeight: "900" }}>{title}</Text>
      </View>
    </View>
  ), [accountId, profile, title]);

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: surface.secondary }} edges={["top", "left", "right"]}>
      <FlatList
        data={accounts}
        keyExtractor={(item) => item.identityId || item.id}
        contentContainerStyle={{ ...readingContent(contentMaxWidths.profile), paddingHorizontal: 16, paddingBottom: 28, flexGrow: 1 }}
        ListHeaderComponent={header}
        renderItem={({ item }) => (
          <Pressable
            onPress={() => router.push(`/profile/${item.identityId || item.id}`)}
            accessibilityRole="button"
            accessibilityLabel={`Ouvrir le profil de ${identityFor(item).displayName}`}
            style={({ pressed }) => ({
              padding: 14,
              marginBottom: 8,
              backgroundColor: surface.primary,
              borderColor: border.subtle,
              borderWidth: 1,
              borderRadius: 16,
              opacity: pressed ? 0.78 : 1,
            })}
          >
            <IdentityHeader identity={identityFor(item)} compact />
          </Pressable>
        )}
        ListEmptyComponent={
          loading || loadingProfile ? (
            <View style={{ padding: 24, alignItems: "center", gap: 8 }}>
              <ActivityIndicator color={uiText.brand} />
              <Text style={{ color: uiText.muted }}>Chargement…</Text>
            </View>
          ) : profileError || !profile ? (
            <View style={{ padding: 16, backgroundColor: surface.primary, borderWidth: 1, borderColor: border.subtle, borderRadius: 16, gap: 10 }}>
              <Text style={{ color: uiText.primary, fontWeight: "800" }}>Profil indisponible.</Text>
              <Pressable onPress={retryProfile} accessibilityRole="button" style={{ alignSelf: "flex-start", paddingVertical: 6 }}>
                <Text style={{ color: uiText.brand, fontWeight: "900" }}>Réessayer</Text>
              </Pressable>
            </View>
          ) : error ? (
            <View style={{ padding: 16, backgroundColor: surface.danger, borderWidth: 1, borderColor: border.danger, borderRadius: 16, gap: 10 }}>
              <Text style={{ color: uiText.danger, fontWeight: "800" }}>{messageForError(error)}</Text>
              <Pressable onPress={retry} accessibilityRole="button" style={{ alignSelf: "flex-start", paddingVertical: 6 }}>
                <Text style={{ color: uiText.danger, fontWeight: "900" }}>Réessayer</Text>
              </Pressable>
            </View>
          ) : (
            <View style={{ padding: 16, backgroundColor: surface.primary, borderWidth: 1, borderColor: border.subtle, borderRadius: 16 }}>
              <Text style={{ color: uiText.muted }}>{emptyLabel}</Text>
            </View>
          )
        }
        ListFooterComponent={
          error && accounts.length ? (
            <Pressable onPress={retry} accessibilityRole="button" style={{ alignSelf: "center", paddingHorizontal: 14, paddingVertical: 10 }}>
              <Text style={{ color: uiText.danger, fontWeight: "800" }}>{messageForError(error)} · Réessayer</Text>
            </Pressable>
          ) : hasMore ? (
            <Pressable
              onPress={() => void loadMore()}
              disabled={loadingMore}
              accessibilityRole="button"
              accessibilityLabel="Charger plus de profils"
              style={{ alignSelf: "center", minHeight: 38, justifyContent: "center", paddingHorizontal: 14, marginTop: 8, borderRadius: 12, borderWidth: 1, borderColor: border.brand, backgroundColor: surface.selected, opacity: loadingMore ? 0.65 : 1 }}
            >
              <Text style={{ color: uiText.primary, fontWeight: "800" }}>{loadingMore ? "Chargement…" : "Charger plus"}</Text>
            </Pressable>
          ) : null
        }
      />
    </SafeAreaView>
  );
}
